import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { FilePdf, MagnifyingGlass, ArrowUUpLeft, Coins } from "@phosphor-icons/react";

const STATUS_BADGE = {
  open: { label: "OPEN", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "PARTIALLY USED", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  settled: { label: "SETTLED", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  refunded: { label: "REFUNDED", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function CreditNotes() {
  const { storeId } = useParams();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    const r = await api.get(`/store/${storeId}/credit-notes`);
    setList(r.data || []);
  };
  useEffect(() => { load(); }, [storeId]);

  const openPdf = async (id) => {
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/credit-notes/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return list.filter((cn) => {
      if (statusFilter !== "all" && cn.status !== statusFilter) return false;
      if (!term) return true;
      return [cn.cn_no, cn.customer_name, cn.invoice_no, cn.reason]
        .some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [list, q, statusFilter]);

  const summary = useMemo(() => ({
    openBalance: list.reduce((a, cn) => a + (cn.refund_method === "store_credit" ? (cn.balance || 0) : 0), 0),
    totalCredited: list.reduce((a, cn) => a + (cn.amount || 0), 0),
    openCount: list.filter((cn) => cn.status === "open" || cn.status === "partial").length,
  }), [list]);

  return (
    <Shell>
      <PageHeader
        title="Credit Notes"
        subtitle="Issued credit notes, their balances and settlements. Store-credit notes can be redeemed as payment at POS."
        testId="credit-notes-header"
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-slate-900 text-white rounded-md p-5" data-testid="cn-open-balance-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold"><Coins size={14} /> Open Store Credit</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2" data-testid="cn-open-balance">{inr(summary.openBalance)}</div>
            <div className="text-[10px] text-slate-400 mt-1">{summary.openCount} redeemable note{summary.openCount === 1 ? "" : "s"}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><ArrowUUpLeft size={14} className="text-amber-600" /> Total Credited</div>
            <div className="font-display text-2xl font-bold font-mono-num mt-2 text-amber-700" data-testid="cn-total-credited">{inr(summary.totalCredited)}</div>
            <div className="text-[10px] text-slate-400 mt-1">All-time · {list.length} credit note{list.length === 1 ? "" : "s"}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">How settlement works</div>
            <div className="text-xs text-slate-600 mt-2 leading-relaxed">
              At <span className="font-semibold">POS Billing</span>, choose payment method
              <span className="font-bold text-emerald-700"> CREDIT NOTE</span> and enter the CN number.
              The balance is applied to the bill and the note is settled automatically.
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cn-search" placeholder="Search CN no, customer, invoice or reason…" className="w-full h-10 pl-9 pr-3 border border-slate-300 rounded-md text-sm" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="cn-status-filter" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-semibold">
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="partial">Partially used</option>
            <option value="settled">Settled</option>
            <option value="refunded">Refunded (cash/UPI)</option>
          </select>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="credit-notes-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">CN #</th>
                <th className="text-left px-4 py-3 font-semibold">Date (IST)</th>
                <th className="text-left px-4 py-3 font-semibold">Customer</th>
                <th className="text-left px-4 py-3 font-semibold">Against</th>
                <th className="text-left px-4 py-3 font-semibold">Refund Via</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-right px-4 py-3 font-semibold">Balance</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">PDF</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-400" data-testid="cn-empty">No credit notes found.</td></tr>}
              {filtered.map((cn) => {
                const b = STATUS_BADGE[cn.status] || STATUS_BADGE.open;
                return (
                  <tr key={cn.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cn-row-${cn.cn_no}`}>
                    <td className="px-4 py-3">
                      <div className="font-mono-num font-semibold text-amber-800">{cn.cn_no}</div>
                      {cn.reason && <div className="text-[10px] text-slate-500 truncate max-w-[180px]">{cn.reason}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{fmtIST(cn.created_at)}</td>
                    <td className="px-4 py-3">{cn.customer_name || "—"}</td>
                    <td className="px-4 py-3 font-mono-num text-slate-600">{cn.invoice_no}</td>
                    <td className="px-4 py-3">
                      <span className="uppercase text-[10px] tracking-widest font-bold text-slate-600">{(cn.refund_method || "—").replace("_", " ")}</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono-num font-bold">{inr(cn.amount)}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-bold text-emerald-700" data-testid={`cn-balance-${cn.cn_no}`}>
                      {cn.refund_method === "store_credit" ? inr(cn.balance) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 border rounded-full text-[10px] uppercase tracking-widest font-bold ${b.cls}`} data-testid={`cn-status-${cn.cn_no}`}>{b.label}</span>
                      {(cn.redemptions || []).length > 0 && (
                        <div className="text-[10px] text-slate-500 font-mono-num mt-1">
                          {(cn.redemptions || []).map((r, i) => (
                            <div key={i}>→ {r.invoice_no} · {inr(r.amount)}</div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openPdf(cn.id)} data-testid={`cn-pdf-${cn.cn_no}`} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-slate-900"><FilePdf size={14} />PDF</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Shell>
  );
}
